import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { StudentFields, SectionTitle, empty } from "@/pages/Students";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status, savedId, ready }) {
  if (status === "saving") return <span className="text-xs text-amber-600 flex items-center gap-1" data-testid="save-status"><Loader2 size={12} className="animate-spin" />Saving…</span>;
  if (status === "saved") return <span className="text-xs text-emerald-600 flex items-center gap-1" data-testid="save-status"><Check size={12} />All changes saved</span>;
  if (status === "error") return <span className="text-xs text-rose-600" data-testid="save-status">Save failed — check the highlighted fields</span>;
  if (!savedId && !ready) return <span className="text-xs text-slate-400" data-testid="save-status">Fill Name, Class & Admission No. to start auto-saving</span>;
  return <span className="text-xs text-slate-400" data-testid="save-status">Changes save automatically</span>;
}

function SiblingsSection({ phone, siblings, onLinkExisting, onAddStub, onRemove, excludeId }) {
  const [stubName, setStubName] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/students", { params: { search: q.trim(), limit: 8 } }).then((r) => setResults(r.data.students || [])).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  const hasPhone = Boolean((phone || "").trim());
  const visibleResults = results.filter((s) => s.id !== excludeId && !siblings.some((x) => x.id === s.id));
  return (
    <div className="mt-2" data-testid="siblings-section">
      <SectionTitle>Siblings</SectionTitle>
      {!hasPhone ? (
        <p className="text-sm text-slate-400" data-testid="siblings-hint">Enter a Phone No. above to group and manage siblings sharing the same number.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="siblings-list">
            {siblings.length === 0 && <span className="text-sm text-slate-400">No other students linked to this phone yet.</span>}
            {siblings.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`sibling-${s.id}`}>
                {s.name}{s.class_name ? <span className="text-indigo-400">· {s.class_name}</span> : null}
                <button type="button" onClick={() => onRemove(s)} className="h-5 w-5 rounded-full hover:bg-indigo-200 flex items-center justify-center" data-testid={`sibling-remove-${s.id}`}><X size={13} /></button>
              </span>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500">Link an existing student</label>
              <div className="relative">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or admission no." className="h-9" data-testid="sibling-search-input" />
                {visibleResults.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto" data-testid="sibling-search-results">
                    {visibleResults.map((s) => (
                      <button key={s.id} type="button" onClick={() => { onLinkExisting(s.id); setQ(""); setResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between gap-2" data-testid={`sibling-result-${s.id}`}>
                        <span className="truncate">{s.name}</span><span className="text-slate-400 shrink-0">{s.admission_number}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Add a new sibling</label>
              <div className="flex gap-2">
                <Input value={stubName} onChange={(e) => setStubName(e.target.value)} placeholder="Sibling's name" className="h-9" data-testid="sibling-stub-input" />
                <Button type="button" variant="outline" className="h-9 shrink-0" onClick={() => { onAddStub(stubName); setStubName(""); }} data-testid="sibling-add-stub-btn">Add</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function StudentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [f, setF] = useState(empty);
  const [savedId, setSavedId] = useState(id || null);
  const [classes, setClasses] = useState([]);
  const [tags, setTags] = useState([]);
  const [colors, setColors] = useState({});
  const [status, setStatus] = useState("idle");
  const [loading, setLoading] = useState(editing);
  const [siblings, setSiblings] = useState([]);
  const skipNextSave = useRef(true);
  const saveSeq = useRef(0);
  const creatingRef = useRef(false);
  const createdIdRef = useRef(null);
  const savedIdRef = useRef(savedId); savedIdRef.current = savedId;
  const lastPhone = useRef(null);

  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/settings/tags").then((r) => { setTags(r.data.tags || []); setColors(r.data.colors || {}); }).catch(() => {});
  }, []);

  const addTagName = async () => {
    const name = window.prompt("New tag name")?.trim();
    if (!name) return "";
    try { const r = await api.post("/settings/tags", { tag: name }); setTags(r.data.tags); setColors(r.data.colors || {}); return name; }
    catch { return ""; }
  };

  useEffect(() => {
    if (!id) { skipNextSave.current = true; setSavedId(null); return; }
    if (id === createdIdRef.current) { setLoading(false); return; } // just auto-created locally; keep local edits
    setLoading(true);
    api.get(`/students/${id}`).then((r) => {
      const d = r.data;
      const t = (d.tags && d.tags.length) ? d.tags : (d.tag ? [d.tag] : []);
      skipNextSave.current = true;
      setF({ ...empty, ...d, tags: t });
      setSavedId(id);
    }).catch(() => toast.error("Failed to load student"))
      .finally(() => setLoading(false));
  }, [id]);

  const doSave = useCallback(async () => {
    const payload = { ...f, tags: f.tags || [], tag: (f.tags && f.tags[0]) || "", promise_amount: Number(f.promise_amount) || 0 };
    const seq = ++saveSeq.current;
    const sid = savedIdRef.current;
    if (!sid) {
      if (creatingRef.current) return;
      if (!(f.name && f.class_id && f.admission_number)) { setStatus("idle"); return; }
      creatingRef.current = true;
      setStatus("saving");
      try {
        const res = await api.post("/students", payload);
        savedIdRef.current = res.data.id;
        createdIdRef.current = res.data.id;
        setSavedId(res.data.id);
        setStatus("saved");
        nav(`/students/${res.data.id}/edit`, { replace: true });
      } catch (e) { setStatus("error"); toast.error(apiErr(e.response?.data?.detail)); }
      finally { creatingRef.current = false; }
    } else {
      setStatus("saving");
      try {
        await api.put(`/students/${sid}`, payload);
        if (seq !== saveSeq.current) return;
        setStatus("saved");
      } catch (e) { setStatus("error"); toast.error(apiErr(e.response?.data?.detail)); }
    }
  }, [f, nav]);

  useEffect(() => {
    if (loading) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    const t = setTimeout(() => { doSave(); }, 800);
    return () => clearTimeout(t);
  }, [f, loading, doSave]);

  const refreshByPhone = useCallback(async (phoneArg) => {
    const phone = (phoneArg ?? "").trim();
    if (!phone) { setSiblings([]); return; }
    try {
      const r = await api.get("/students/lookup/by-phone", { params: { phone, exclude: savedIdRef.current || undefined } });
      setSiblings(r.data.students || []);
      const af = r.data.autofill || {};
      setF((p) => {
        const next = { ...p };
        let changed = false;
        const isEmpty = (k, cur) => !cur || (k === "category" && cur === "General") || (k === "nationality" && cur === "Indian");
        Object.entries(af).forEach(([k, v]) => { if (v && isEmpty(k, next[k])) { next[k] = v; changed = true; } });
        return changed ? next : p;
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const phone = (f.phone || "").trim();
    if (lastPhone.current === null) { lastPhone.current = phone; if (phone) refreshByPhone(phone); return; }
    if (phone === lastPhone.current) return;
    const t = setTimeout(() => { lastPhone.current = phone; refreshByPhone(phone); }, 600);
    return () => clearTimeout(t);
  }, [f.phone, refreshByPhone]);

  const linkExisting = async (studentId) => {
    const phone = (f.phone || "").trim();
    if (!phone) { toast.error("Enter a phone number first"); return; }
    try { await api.post(`/students/${studentId}/set-phone`, { phone }); toast.success("Sibling linked"); await refreshByPhone(phone); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addStub = async (name) => {
    const phone = (f.phone || "").trim();
    if (!phone) { toast.error("Enter a phone number first"); return; }
    if (!name.trim()) return;
    try { await api.post("/students", { ...empty, name: name.trim(), phone, promise_amount: 0, admission_number: `TMP-${Date.now()}` }); toast.success("Sibling added"); await refreshByPhone(phone); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeSibling = async (sib) => {
    if (!window.confirm(`Unlink ${sib.name} from this phone number? Their phone number will be cleared (the student is not deleted).`)) return;
    try { await api.post(`/students/${sib.id}/set-phone`, { phone: "" }); toast.success("Sibling unlinked"); await refreshByPhone((f.phone || "").trim()); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const siblingsSlot = (
    <SiblingsSection phone={f.phone} siblings={siblings} onLinkExisting={linkExisting} onAddStub={addStub} onRemove={removeSibling} excludeId={savedId} />
  );

  if (loading) return <div className="p-16 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" />Loading…</div>;

  const ready = Boolean(f.name && f.class_id && f.admission_number);

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="student-editor-page">
      <div className="bg-white border-b border-slate-200 py-3 flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">{savedId ? "Edit Student" : "Add Student"}</h1>
          <StatusPill status={status} savedId={savedId} ready={ready} />
        </div>
        <Button variant="ghost" size="icon" onClick={() => nav("/students")} data-testid="close-editor-btn" className="rounded-full h-10 w-10"><X size={22} /></Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-2">
        <StudentFields f={f} set={set} setF={setF} classes={classes} tags={tags} colors={colors} onAddTag={addTagName} siblingsSlot={siblingsSlot} />
      </div>
    </div>
  );
}
